import React, { useState } from "react";
import { X, Scale, Trash2 } from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS } from "../../lib/constants";

const fmtArea = (p) => (p.area_sqft ? `${Number(p.area_sqft).toLocaleString()} sq ft${p.area_acres ? ` (${p.area_acres} ac)` : ""}` : "—");
const fmtFront = (p) => (p.frontage_m ? `${p.frontage_m} m` : "—");
const fmtBuild = (p) => (p.buildable_sqft ? `${Number(p.buildable_sqft).toLocaleString()} sq ft` : "—");
const label = (p) => `Lot ${p.lot_number ?? p.parcel_no}${p.block ? ` · Blk ${p.block}` : ""}`;

const ROWS = [
  ["Status", (p) => <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[p.status] }} />{STATUS_LABELS[p.status]}</span>],
  ["Lot area", fmtArea],
  ["Frontage", fmtFront],
  ["Buildable", fmtBuild],
  ["Walkout", (p) => p.walkout || "No"],
  ["Lot type", (p) => (p.features && p.features.length ? p.features.join(", ") : "Standard")],
  ["Builder", (p) => p.builder_name || "—"],
];

const LotCompare = ({ items, onRemove, onClear }) => {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;

  return (
    <>
      {/* sticky tray */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0C1510]/95 backdrop-blur border-t border-[#C9B79C]/25" data-testid="compare-tray">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-3 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 text-[#C9B79C] text-xs uppercase tracking-wide"><Scale size={15} /> Compare</span>
          <div className="flex flex-wrap gap-2 flex-1 min-w-0">
            {items.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 bg-[#1B2E24] text-[#F3EEE3] text-xs px-2.5 py-1 rounded-full">
                {label(p)}
                <button onClick={() => onRemove(p)} data-testid={`compare-remove-${p.parcel_no}`} className="text-[#F3EEE3]/50 hover:text-[#F3EEE3]"><X size={12} /></button>
              </span>
            ))}
          </div>
          <button onClick={onClear} className="text-[#F3EEE3]/50 hover:text-[#F3EEE3] text-xs inline-flex items-center gap-1"><Trash2 size={13} /> Clear</button>
          <button onClick={() => setOpen(true)} disabled={items.length < 2} data-testid="compare-open" className="inline-flex items-center gap-2 bg-[#F3EEE3] text-[#12201A] px-5 py-2 rounded-full text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white transition-colors">
            Compare {items.length}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="compare-dialog">
          <div className="absolute inset-0 bg-[#0C1510]/85 backdrop-blur-sm" onMouseDown={() => setOpen(false)} />
          <div className="relative w-full max-w-3xl bg-[#12201A] border border-[#F3EEE3]/15 rounded-2xl p-7 max-h-[90vh] overflow-auto" onMouseDown={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[#F3EEE3]/60 hover:text-[#F3EEE3]" data-testid="compare-close"><X size={20} /></button>
            <p className="eyebrow text-[#C9B79C]">Side-by-side</p>
            <h3 className="font-display font-light text-3xl text-[#F3EEE3] mt-1 mb-5">Compare lots</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="text-left text-[#F3EEE3]/45 font-normal py-3 pr-4 align-bottom text-xs uppercase tracking-wide">Attribute</th>
                    {items.map((p) => (
                      <th key={p.id} className="text-left text-[#F3EEE3] font-medium py-3 px-4 whitespace-nowrap">{label(p)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([name, fn]) => (
                    <tr key={name} className="border-t border-[#F3EEE3]/10">
                      <td className="text-[#F3EEE3]/50 py-3 pr-4 whitespace-nowrap">{name}</td>
                      {items.map((p) => (
                        <td key={p.id} className="text-[#F3EEE3] py-3 px-4">{fn(p)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LotCompare;
